import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserCircle2, Info, Shield, Share2, Scale, ChevronDown } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { NotificationBell } from "./NotificationBell";
import { ThemeToggle } from "./ThemeToggle";
import { InstallButton } from "./InstallButton";
import logo from "@/assets/logo.png";
import { getShareBaseUrl } from "@/lib/seoUtils";
import { useUserRole } from "@/hooks/useUserRole";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileMenu } from "./MobileMenu";

export const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { isAdmin } = useUserRole();
  const isMobile = useIsMobile();

  const handleShare = async () => {
    const url = getShareBaseUrl();
    const shareData = {
      title: 'MiamSécur',
      text: 'Alertes risques pour des repas sereins - Soyez informé des rappels de produits alimentaires',
      url: url
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url);
        toast({
          title: "Lien copié",
          description: "Le lien de MiamSécur a été copié dans le presse-papiers",
        });
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        toast({
          title: "Erreur",
          description: "Impossible de partager l'application",
          variant: "destructive"
        });
      }
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b shadow-soft">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo and title */}
          <a 
            href="/"
            className="flex items-center gap-3 cursor-pointer no-underline"
            role="link"
            aria-label="MiamSécur — Retour à l'accueil"
            onClick={(e) => {
              e.preventDefault();
              if (location.pathname === '/') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              } else {
                navigate('/');
              }
            }}
          >
            <img src={logo} alt="MiamSécur" className="h-10 w-10 object-contain" width={40} height={40} loading="eager" decoding="async" fetchPriority="high" />
            <div>
              <span className="block text-lg font-bold text-foreground">MiamSécur</span>
              {!isMobile && (
                <p className="text-xs text-muted-foreground">Alertes risques pour des repas sereins</p>
              )}
            </div>
          </a>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {isMobile ? (
              <>
                <InstallButton variant="ghost" showLabel={false} size="icon" />
                <NotificationBell />
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate('/settings')}
                  aria-label="Profil et paramètres"
                >
                  <UserCircle2 className="h-5 w-5" />
                </Button>
                <MobileMenu
                  isAdmin={isAdmin}
                  currentPath={location.pathname}
                  onNavigate={navigate}
                  onShare={handleShare}
                />
              </>
            ) : (
              <>
                {/* Groupe 1 : Navigation & Info */}
                <Button 
                  variant={location.pathname === '/about' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => navigate('/about')}
                  aria-label="À propos"
                >
                  <Info className="h-4 w-4 mr-1" />
                  <span className="text-xs">À propos</span>
                </Button>
                <Button 
                  variant="ghost"
                  size="sm"
                  onClick={handleShare}
                  aria-label="Partager l'application"
                >
                  <Share2 className="h-4 w-4 mr-1" />
                  <span className="text-xs">Partager</span>
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant={
                        ['/confidentialite', '/cgu', '/mentions-legales'].includes(location.pathname)
                          ? 'secondary'
                          : 'ghost'
                      }
                      size="sm"
                      aria-label="Informations légales"
                    >
                      <Scale className="h-4 w-4 mr-1" />
                      <span className="text-xs">Légal</span>
                      <ChevronDown className="h-3 w-3 ml-1 opacity-70" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-popover">
                    <DropdownMenuItem onClick={() => navigate('/confidentialite')}>
                      Politique de confidentialité
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/cgu')}>
                      Conditions générales d'utilisation
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/mentions-legales')}>
                      Mentions légales
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <InstallButton variant="default" showLabel={true} size="sm" />
                {isAdmin && (
                  <Button 
                    variant={location.pathname === '/admin' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => navigate('/admin')}
                    aria-label="Admin"
                  >
                    <Shield className="h-4 w-4 mr-1" />
                    <span className="text-xs">Admin</span>
                  </Button>
                )}
                
                {/* Groupe 2 : Utilitaires personnels */}
                <ThemeToggle />
                <NotificationBell />
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate('/settings')}
                  aria-label="Profil et paramètres"
                >
                  <UserCircle2 className="h-5 w-5" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};